import { Component, EventEmitter, OnInit, Output, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SociosService } from '../../../services/socios.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { SpinnerService } from '../../../services/spinner.service';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { DependenteRequest, Socio } from '../../../models/sociosRequest.model';
import { SexoEnum } from '../../../models/enums/socios/Sexo.enum';
import { RacaCorEnum } from '../../../models/enums/socios/RacaCorEnum.enum';
import { EstadoCivilEnum } from '../../../models/enums/socios/estadoCivil.enum';
import { GrauInstrucaoEnum } from '../../../models/enums/socios/grauInstrucao.enum';
import { LocationService } from '../../../services/location-service.service';
import { catchError, finalize, tap, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { Estado } from '../../../models/interfaces/estado.interface';
import { TipoDependenteEnum } from '../../../models/enums/tipoDependente.enum';
import { AuthService } from '../../../services/auth.service';
import { PaisesEsocialService } from '../../../services/paises-esocial.service';
import { SocioRequest } from '../../../models/interfaces/socioRequest.interface';

@Component({
  selector: 'app-socios-adicionar',
  templateUrl: './socios-adicionar.component.html',
  styleUrls: ['./socios-adicionar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SociosAdicionarComponent implements OnInit, OnDestroy {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  private destroy$ = new Subject<void>();
  userId: string;

  sexoOptions = this.enumToDropdownOptions(SexoEnum);
  racaCorOptions = this.enumToDropdownOptions(RacaCorEnum);
  estadoCivilOptions = this.enumToDropdownOptions(EstadoCivilEnum);
  grauInstrucaoOptions = this.enumToDropdownOptions(GrauInstrucaoEnum);
  tipoDependenteOptions = this.enumToDropdownOptions(TipoDependenteEnum);
  simNaoOptions = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];
  tipoLogradouros = [
    { label: 'Rua', value: 'R' },
    { label: 'Avenida', value: 'AV' },
    { label: 'Travessa', value: 'TV' },
    { label: 'Alameda', value: 'AL' },
    { label: 'Praça', value: 'PV' },
    { label: 'Praia', value: 'PR' },
    { label: 'Estrada', value: 'EST' }
  ];
  paisesOptions: { label: string; value: string }[] = [];
  diasPagamentoOptions: { label: string, value: number }[] = [];

  estados: Estado[] = [];
  municipios: { label: string; value: string }[] = [];
  isSubmitting = false;
  proLabore: boolean = false;
  
  private readonly currentYear = new Date().getFullYear();

  private isDestroying = false;
  private dependenteWarningShown = false;

  socioForm = new FormGroup({
    tpInsc: new FormControl(1),
    nrInsc: new FormControl(''),
    cpfTrab: new FormControl('', [
      Validators.required, 
      Validators.pattern(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/)
    ]),    
    nmTrab: new FormControl('', Validators.required),
    sexo: new FormControl(null, Validators.required),
    racaCor: new FormControl(null, Validators.required),
    estCiv: new FormControl(null, Validators.required),
    grauInstr: new FormControl(null, Validators.required),
    nmSoc: new FormControl(''),
    dtNascto: new FormControl('', Validators.required),
    paisNascto: new FormControl('', Validators.required),
    tpLograd: new FormControl('', Validators.required),
    dscLograd: new FormControl('', Validators.required),
    nrLograd: new FormControl('', Validators.required),
    complemento: new FormControl(''),
    bairro: new FormControl('', Validators.required),
    cep: new FormControl('', Validators.required),
    uf: new FormControl('', Validators.required),
    codMunic: new FormControl('', Validators.required),
    fonePrinc: new FormControl('', Validators.required),
    emailPrinc: new FormControl('', [Validators.required, Validators.email]),
    cadIni: new FormControl(false),
    matricula: new FormControl('1'),
    codCateg: new FormControl('723'),
    dtInicio: new FormControl('', Validators.required),
    nmCargo: new FormControl('Diretor Administrativo'),
    cboCargo: new FormControl('123105'),
    proLabore: new FormControl(false),
    participacaoSocietaria: new FormControl(null),
    vrSalFx: new FormControl({ value: null, disabled: true }),
    inssAproximado: new FormControl({ value: null as number | null, disabled: true }),
    baseInss: new FormControl({ value: null as number | null, disabled: true }),
    contribuicaoInss: new FormControl({ value: null as number | null, disabled: true }),
    undSalFixo: new FormControl(5),
    usuarioId: new FormControl(''),
    possuiDependentes: new FormControl(false),
    numeroDependentes: new FormControl({ value: null, disabled: true }),
    dependentes: new FormArray([]),
    duploVinculo: new FormControl({ value: null, disabled: true }),
    diaPagamentoProLabore: new FormControl({ value: null, disabled: true }),
    dataInicioValidade: new FormControl<Date | null>({ value: null, disabled: true })
  });

  constructor(
    private paisesEsocialService: PaisesEsocialService,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private sociosService: SociosService,
    private router: Router,
    private jwtHelper: JwtHelper,
    private locationService: LocationService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Cadastrar Sócios');
    this.iconeChange.emit('pi pi-plus');
    
    this.initializeBasicData();

    this.loadAsyncData();
  }

  ngOnDestroy(): void {
    this.isDestroying = true;
    this.destroy$.next();
    this.destroy$.complete();

    this.estados = [];
    this.municipios = [];
    this.paisesOptions = [];
    this.diasPagamentoOptions = [];

    if (this.socioForm) {
      this.socioForm.reset();
      this.socioForm = null as any;
    }
    
    if (typeof window !== 'undefined' && (window as any).gc) {
      (window as any).gc();
    }
  }

  private initializeBasicData(): void {
    this.socioForm.get('usuarioId')?.setValue(this.userId);
    this.paisesOptions = this.paisesEsocialService.getPaises();
    
    this.diasPagamentoOptions = Array.from({ length: 31 }, (_, i) => ({
      label: `${i + 1}`,
      value: i + 1
    }));
    this.socioForm.get('numeroDependentes')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dependenteWarningShown = false;
      });
  }

  private loadAsyncData(): void {
    this.InicializarDadosIBGE();
    
    this.sociosService.obterProximaMatricula(this.userId)
      .pipe(
        takeUntil(this.destroy$),
        tap(response => {
          this.socioForm.patchValue({
            matricula: response.proximaMatricula.toString()
          });
          this.cdr.detectChanges();
        }),
        catchError(error => {
          console.error('Erro ao obter próxima matrícula:', error);
          return [];
        })
      )
      .subscribe();
    
    setTimeout(() => {
      this.carregarDadosUsuario();
    }, 100);
  }

  private enableAllFields() {
    Object.keys(this.socioForm.controls).forEach(key => {
      const control = this.socioForm.get(key);
      if (control?.disabled) {
        control.enable();
      }
    });
    
    const dependentesArray = this.socioForm.get('dependentes') as FormArray;
    dependentesArray.controls.forEach(dependenteGroup => {
      if (dependenteGroup instanceof FormGroup) {
        Object.keys(dependenteGroup.controls).forEach(key => {
          const control = dependenteGroup.get(key);
          if (control?.disabled) {
            control.enable();
          }
        });
      }
    });
  }
  

  isDependenteCountValid(): boolean {
    const possuiDependentes = this.socioForm.get('possuiDependentes')?.value;
    if (!possuiDependentes) {
      this.dependenteWarningShown = false;
      return true;
    }
    
    const numeroDependentes = this.socioForm.get('numeroDependentes')?.value;
    const dependentesArray = this.dependentes;
    
    const isValid = numeroDependentes === dependentesArray.length;

    if (isValid) {
      this.dependenteWarningShown = false;
    }
    
    return isValid;
  }

  onMonthSelect(value: Date) {
    if (this.isDestroying) return;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      this.socioForm.get('dataInicioValidade')?.setValue(
        new Date(value.getFullYear(), value.getMonth(), 1),
        { emitEvent: false }
      );
    }

    requestAnimationFrame(() => {
      if (this.isDestroying) return;
      
      const calendar = document.querySelector('#dataInicioValidade .p-datepicker') as HTMLElement;
      if (calendar && !this.isDestroying) {
        calendar.classList.add('hidden'); 
      }
    });
  }
  

  onProLaboreChange(isProLabore: boolean) {
    const camposFinanceiros = [
      'vrSalFx',
      'inssAproximado',
      'baseInss',
      'contribuicaoInss',
      'duploVinculo',
      'diaPagamentoProLabore',
      'dataInicioValidade'
    ];
  
    camposFinanceiros.forEach(campo => {
      const control = this.socioForm.get(campo);
      if (!isProLabore) {
        control?.disable();
        control?.setValue(null);
        control?.clearValidators();
      } else {
        control?.enable();
        if (campo !== 'inssAproximado' && campo !== 'baseInss' && campo !== 'contribuicaoInss') {
          control?.setValidators(Validators.required);
        }
      }
      control?.updateValueAndValidity();
    });
    
    if (!isProLabore) {
      this.resetFinancialFields();
    }
  }
  

  get dependentes(): FormArray {
    return this.socioForm.get('dependentes') as FormArray;
  }

  addDependente(): void {
    if (this.isDestroying) return;
    
    this.dependenteWarningShown = false;
    
    const dependenteGroup = new FormGroup({
      tpDep: new FormControl(null, Validators.required),
      nmDep: new FormControl('', Validators.required),
      dtNasctoDep: new FormControl('', Validators.required),
      cpfDep: new FormControl('', Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/)),
      depIRRF: new FormControl(false),
      depSF: new FormControl(false),
      incTrab: new FormControl(false),
      descrDep: new FormControl({ value: '', disabled: true }, Validators.maxLength(99)),
    });

    dependenteGroup.get('tpDep')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.isDestroying) return;
        
        const descrDepControl = dependenteGroup.get('descrDep');
        if (value === TipoDependenteEnum.Outros) {
          descrDepControl?.setValidators([Validators.required, Validators.maxLength(99)]);
          descrDepControl?.enable();
        } else {
          descrDepControl?.clearValidators();
          descrDepControl?.disable();
        }
        descrDepControl?.updateValueAndValidity();
        this.cdr.markForCheck();
      });

    this.dependentes.push(dependenteGroup);
    this.cdr.markForCheck();
  }

  getDescrDepError(control: AbstractControl | null): string {
    if (!control) return '';
    if (control.hasError('required')) return 'Descrição é obrigatória para o tipo "Outros".';
    if (control.hasError('maxlength')) return 'Descrição não pode ter mais de 99 caracteres.';
    return '';
  }

  private formatEnumLabel(enumKey: string): string {
    return enumKey.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private enumToDropdownOptions(enumObj: any): { label: string; value: number | string }[] {
    const keys = Object.keys(enumObj).filter((key) => isNaN(Number(key)));
    return keys.map((key) => ({
      label: this.formatEnumLabel(key),
      value: enumObj[key]
    }));
  }

  private carregarDadosUsuario(): void {
    if (this.isDestroying) return;
    
    this.spinnerService.show('Carregando dados do usuário...');
    this.authService.obterUsuarioPorId(this.userId).pipe(
      takeUntil(this.destroy$),
      tap((user) => {
        if (this.isDestroying) return;
        
        if (user && user.cpfCnpj) {
          const cnpjPrimeiros8 = user.cpfCnpj;
          this.socioForm.patchValue({
            nrInsc: cnpjPrimeiros8,
          });
        } else {
          this.toastService.setMensagemToast(
            'CNPJ não encontrado ou inválido.',
            SeveridadeToast.Error
          );
        }
      }),
      catchError((error) => {
        if (!this.isDestroying) {
          this.toastService.setMensagemToast(
            'Erro ao carregar os dados do usuário.',
            SeveridadeToast.Error
          );
        }
        console.error('Erro ao carregar dados do usuário:', error);
        return [];
      }),
      finalize(() => {
        if (!this.isDestroying) {
          this.spinnerService.hide();
        }
      })
    ).subscribe();
  }

  onPossuiDependentesChange(possuiDependentes: boolean): void {
    const numeroDependentesControl = this.socioForm.get('numeroDependentes');
  
    if (possuiDependentes) {
      numeroDependentesControl?.enable();
    } else {
      numeroDependentesControl?.disable(); 
      numeroDependentesControl?.setValue(null);
      this.clearDependentes();
    }
  }
  
  clearDependentes(): void {
    const dependentesArray = this.socioForm.get('dependentes') as FormArray;
    dependentesArray.clear();
  }
  
  checkDependentesBeforeSave(): boolean {
    return this.isDependenteCountValid();
  }

  submitForm(): void {
    if (this.isDestroying) return;
    
    if (!this.isDependenteCountValid()) {
      if (!this.dependenteWarningShown) {
        const dependentesArray = this.dependentes;
        const numeroDependentes = this.socioForm.get('numeroDependentes')?.value;
        
        this.toastService.setMensagemToast(
          `O número de dependentes adicionados (${dependentesArray.length}) não corresponde ao informado (${numeroDependentes}).`,
          SeveridadeToast.Warn
        );
        this.dependenteWarningShown = true;
      }
      return;
    }
  
    if (this.socioForm.invalid) {
        this.socioForm.markAllAsTouched();
        this.toastService.setMensagemToast(
            'Por favor, preencha todos os campos obrigatórios.',
            SeveridadeToast.Warn
        );
        return;
    }
  
    this.enableAllFields();
    const formValues = this.socioForm.value;
    if (formValues.possuiDependentes) {
      formValues.numeroDependentes = this.dependentes.length;
    } else {
      formValues.numeroDependentes = 0;
    }
    

    
    const socio: Socio = this.formatDatesInForm(formValues);
    this.isSubmitting = true;
    this.spinnerService.show('Enviando dados do sócio...');
  
    this.sociosService.saveSocioBd(socio)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
            if (this.isDestroying) return;
            
            this.toastService.setMensagemToast(
                'Sócio cadastrado com sucesso!',
                SeveridadeToast.Success
            );
            this.router.navigate(['/socios']);
        },
        error: (err) => {
            if (!this.isDestroying) {
                this.toastService.setMensagemToast(
                    'Erro ao cadastrar sócio. Tente novamente.',
                    SeveridadeToast.Error
                );
                console.error('Erro ao cadastrar sócio:', err);
                this.spinnerService.hide();
                this.router.navigate(['/socios']);
            }
        },
        complete: () => {
            if (!this.isDestroying) {
                this.isSubmitting = false;
                this.spinnerService.hide();
            }
        },
    });
  }
  

  async InicializarDadosIBGE() {
    if (this.isDestroying) return;
    
    this.spinnerService.show('Carregando estados...');
    this.locationService
      .getEstados()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (!this.isDestroying) {
            this.spinnerService.hide();
          }
        })
      )
      .subscribe((estados) => {
        if (this.isDestroying) return;
        
        this.estados = estados
          .map((estado: any) => ({
            label: estado.sigla,
            value: estado.sigla,
            id: estado.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        
        this.cdr.markForCheck();
      });
  
    this.socioForm.get('uf')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((ufSigla) => {
        if (this.isDestroying) return;
        
        const estado = this.estados.find((e) => e.value === ufSigla);
        if (estado) {
          this.spinnerService.show('Carregando municípios...');
          this.locationService
            .getMunicipiosPorUf(estado.id)
            .pipe(
              takeUntil(this.destroy$),
              finalize(() => {
                if (!this.isDestroying) {
                  this.spinnerService.hide();
                }
              })
            )
            .subscribe((municipios) => {
              if (this.isDestroying) return;
              
              if (municipios && municipios.length > 0) {
                this.municipios = municipios
                  .map((municipio: any) => ({
                    label: municipio.nome,
                    value: municipio.id.toString(),
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label));
                this.socioForm.get('codMunic')?.setValue('');
                this.cdr.markForCheck();
              } else {
                this.municipios = [];
                this.cdr.markForCheck();
              }
            });
        } else {
          this.municipios = [];
          this.socioForm.get('codMunic')?.setValue('');
          this.cdr.markForCheck();
        }
      });
  }
  

  onCancel() {
    this.router.navigate(['/socios']);
  }

  removeDependente(index: number) {
    const dependentes = this.socioForm.get('dependentes') as FormArray;
    dependentes.removeAt(index);
    this.dependenteWarningShown = false;
  }

  onVrSalFxBlur() {
    const valorProLabore = this.socioForm.get('vrSalFx')?.value;
    const numeroDependentes = this.socioForm.get('numeroDependentes')?.value || 0;

    if (valorProLabore !== null && valorProLabore !== undefined && valorProLabore > 0) {
      this.calcularProLabore(valorProLabore, numeroDependentes);
    } else if (valorProLabore === null || valorProLabore === undefined || valorProLabore === 0) {
      this.resetFinancialFields();
    }
  }

  recalcularProLabore() {
    const valorProLabore = this.socioForm.get('vrSalFx')?.value;
    const numeroDependentes = this.socioForm.get('numeroDependentes')?.value || 0;
    if (valorProLabore !== null && valorProLabore !== undefined && valorProLabore > 0) {
      this.calcularProLabore(valorProLabore, numeroDependentes);
    } else {
      this.toastService.setMensagemToast(
        'Informe um valor válido para o Pró-Labore antes de recalcular.',
        SeveridadeToast.Warn
      );
    }
  }
  
  calcularProLabore(valorProLabore: number, numeroDependentes: number) {
    if (this.isDestroying) return;
    
    this.spinnerService.show('Calculando Pró-Labore...');
    
    this.sociosService.calcularProLabore(valorProLabore, numeroDependentes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.isDestroying) return;

          this.socioForm.get('inssAproximado')?.setValue(response.inss);
          this.socioForm.get('baseInss')?.setValue(response.irrf);
          this.socioForm.get('contribuicaoInss')?.setValue(response.valorLiquido);
          this.socioForm.get('inssAproximado')?.disable();
          this.socioForm.get('baseInss')?.disable();
          this.socioForm.get('contribuicaoInss')?.disable();
          
          this.spinnerService.hide();
          this.toastService.setMensagemToast(
            'Pró-Labore calculado com sucesso!',
            SeveridadeToast.Success
          );
          this.cdr.markForCheck();
        },
        error: (error) => {
          if (!this.isDestroying) {
            console.error('Erro ao calcular Pró-Labore:', error);
            this.spinnerService.hide();
            this.toastService.setMensagemToast(
              'Erro ao calcular o Pró-Labore. Tente novamente.',
              SeveridadeToast.Error
            );
          }
        }
      });
  }

  resetFinancialFields() {
    this.socioForm.get('inssAproximado')?.setValue(null);
    this.socioForm.get('baseInss')?.setValue(null);
    this.socioForm.get('contribuicaoInss')?.setValue(null);
    this.socioForm.get('inssAproximado')?.enable();
    this.socioForm.get('baseInss')?.enable();
    this.socioForm.get('contribuicaoInss')?.enable();
  }

  isCalculatedField(fieldName: string): boolean {
    const control = this.socioForm.get(fieldName);
    return control?.value && !control.disabled;
  }

  isCalculatedFieldDisabled(fieldName: string): boolean {
    const control = this.socioForm.get(fieldName);
    return control?.disabled || false;
  }

  private formatDatesInForm(formValues: any): any {
    const formattedValues = { ...formValues };

    for (const key in formattedValues) {
      if (formattedValues[key] instanceof Date) {
        if (key === 'dataInicioValidade') {
          const month = (formattedValues[key].getMonth() + 1).toString().padStart(2, '0');
          const year = formattedValues[key].getFullYear();
          formattedValues[key] = `${month}-${year}`;
        } else {
          formattedValues[key] = formattedValues[key].toISOString().split('T')[0];
        }
      } else if (key === 'dataInicioValidade' && formattedValues[key]) {
        const dataInicioValidade = new Date(formattedValues[key]);

        if (!Number.isNaN(dataInicioValidade.getTime())) {
          const month = (dataInicioValidade.getMonth() + 1).toString().padStart(2, '0');
          const year = dataInicioValidade.getFullYear();
          formattedValues[key] = `${month}-${year}`;
        }
      }
    }

    return formattedValues;
  }

}
